import React, { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import {
  HStack,
  Text,
} from '@ayble-health/dashboard-components';
import { INK, SUBTLE } from './tokens';

export type CountTabItem = {
  label: string;
  count: number;
};

export type CountTab = CountTabItem & {
  content: ReactNode;
};

type BarProps = {
  tabs: CountTabItem[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  rightSlot?: ReactNode;
  /** Bottom margin on the separator line. Defaults to 4 (16px). Pass 0 when
   *  the bar is inside a sticky header so it flows flush into the scroll area. */
  mb?: number;
};

type TabsProps = Omit<BarProps, 'tabs'> & { tabs: CountTab[] };

/** Tab bar only — no content panel. Use inside sticky headers. */
export const CountTabBar = ({
  tabs,
  activeIndex,
  setActiveIndex,
  rightSlot,
  mb = 4,
}: BarProps) => (
  <HStack
    justify="space-between"
    align="center"
    borderBottomWidth="1px"
    borderColor="cloud"
    mb={mb}
  >
    <HStack gap={6} align="center">
      {tabs.map((tab, index) => {
        const active = index === activeIndex;
        return (
          <Box
            key={tab.label}
            onClick={() => setActiveIndex(index)}
            cursor="pointer"
            pb={2}
            borderBottomWidth="2px"
            borderColor={active ? 'evergreen' : 'transparent'}
            mb="-1px"
          >
            <HStack gap={2} align="center">
              <Text fontWeight={600} color={active ? INK : SUBTLE}>
                {tab.label}
              </Text>
              <Box
                bg={active ? 'evergreen' : 'cloud'}
                borderRadius="full"
                minW="20px"
                h="18px"
                px="6px"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontSize="2xs"
                  fontWeight={700}
                  color={active ? 'white' : SUBTLE}
                  lineHeight="1"
                >
                  {tab.count}
                </Text>
              </Box>
            </HStack>
          </Box>
        );
      })}
    </HStack>
    {rightSlot ? <Box pb={2}>{rightSlot}</Box> : null}
  </HStack>
);

/** Tab bar + content panel. Drop-in for existing usage. */
export const CountTabs = ({ tabs, activeIndex, setActiveIndex, rightSlot }: TabsProps) => (
  <Box>
    <CountTabBar
      tabs={tabs}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      rightSlot={rightSlot}
    />
    <Box>{tabs[activeIndex].content}</Box>
  </Box>
);
